import { useMutation } from '@tanstack/react-query';
import { getErrorMessage } from '@tectonic/errors';
import { Logger } from '@tectonic/logger';
import {
  requestOtpForUserUpdate,
  updateUser,
} from '@tectonic/remix-client-network';
import { delay } from '@tectonic/utils';
import { isNil, omitBy } from 'lodash-es';
import { useToast } from '../../../core/ElemasonEntry/Toast';
import { useShallow, useUserProfileWidgetStore } from '../../../store';
import { toPhoneText } from '../../../utils';

import type {
  ElemasonUserProfileOtpConfirmationFormWidget,
  Nil,
  UserUpdateOtpRequestPayload,
} from '@tectonic/types';
import type { RefObject } from 'react';
import type { OtpConfirmationFormRef } from '../../../components';
import type { UserProfileWidgetStore } from '../../../store';

const requestOtpMutationFn = async (payload: UserUpdateOtpRequestPayload) => {
  const response = await requestOtpForUserUpdate(payload);
  if (response.error) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw response.error;
  }
};

const updateProfileMutationFn = async (
  payload: UserProfileWidgetStore['details'] & { code: string }
) => {
  const { phoneNumber, ...rest } = omitBy(payload, isNil);
  const phone = toPhoneText(phoneNumber!)!;
  const response = await updateUser({ ...rest, phone });

  if (response.error) {
    throw response.error;
  }
};

const profileSelector = (state: UserProfileWidgetStore) => ({
  details: state.details,
});

const useProfileOtpConfirmationForm = (
  widget: ElemasonUserProfileOtpConfirmationFormWidget,
  formRef: RefObject<Nil<OtpConfirmationFormRef>>
) => {
  const wData = widget.data!;
  const { errorMessages } = wData;
  const { showToast } = useToast();

  const { details } = useUserProfileWidgetStore(useShallow(profileSelector));

  const { isPending: isResendingOtp, mutateAsync: resendOtp } = useMutation({
    mutationKey: [],
    mutationFn: requestOtpMutationFn,
  });

  const { isPending: isSubmitting, mutateAsync: updateProfile } = useMutation({
    mutationFn: updateProfileMutationFn,
    mutationKey: [],
  });

  const onSubmit = async (code: string) => {
    try {
      await updateProfile({ ...details, code });
      showToast({ title: wData.messages.verifySuccess });
      await delay(1000);
      globalThis.location.reload();
    } catch (error) {
      Logger.error('useProfileOtpConfirmationForm:onSubmit', error);
      showToast({ title: getErrorMessage(error, wData.errorMessages) });
    }
  };

  const onResendOtp = async () => {
    try {
      const { phoneNumber } = details;
      const phone = toPhoneText(phoneNumber!)!;
      await resendOtp({ phone });
      showToast({ title: wData.messages.otpResendSuccess });
      formRef.current?.resetTimer();
    } catch (error) {
      showToast({ title: getErrorMessage(error, errorMessages) });
    }
  };

  return { isSubmitting, isResendingOtp, onSubmit, onResendOtp };
};

export { useProfileOtpConfirmationForm };
